/* =========================================================
 * MotorCreditoVisual — Motor de Crédito PF/PJ (super visual)
 * ======================================================= */

import moment from "moment";
import { motion } from "framer-motion";
import {
  HiCheck,
  HiExclamation,
  HiThumbDown,
  HiThumbUp,
  HiX,
} from "react-icons/hi";
import { formatDocument } from "../../../utils/utils";

const RISK_COLORS = {
  BAIXO: "from-emerald-500 to-teal-500",
  MÉDIO: "from-amber-500 to-yellow-500",
  ALTO: "from-orange-500 to-red-500",
  ALTÍSSIMO: "from-rose-600 to-red-700",
};
const RISK_TEXT = {
  BAIXO: "text-emerald-700",
  MÉDIO: "text-amber-700",
  ALTO: "text-orange-700",
  ALTÍSSIMO: "text-rose-700",
};

function numberBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function percentBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n) * 100; // espera índice 0–1
  return `${x.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}
function moneyBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  return x.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function maskCNPJ(v = "") {
  const s = (v || "").replace(/\D/g, "").slice(0, 14);
  return s
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}
function maskCEP(v = "") {
  const s = (v || "").replace(/\D/g, "").slice(0, 8);
  return s.replace(/^(\d{5})(\d{1,3})$/, "$1-$2");
}
function companyAge(isoOrBr) {
  if (!isoOrBr) return "—";
  // aceita "YYYY-MM-DD" ou "DD/MM/YYYY"
  let m = isoOrBr.includes("/")
    ? moment(isoOrBr, "DD/MM/YYYY")
    : moment(isoOrBr);
  if (!m.isValid()) return "—";
  const years = moment().diff(m, "years");
  return `${years} ano${years === 1 ? "" : "s"}`;
}
function addressLine(dc = {}) {
  const { endereco, bairro, cidade, uf, cep } = dc;
  const parts = [
    endereco,
    bairro ? `Bairro ${bairro}` : null,
    cidade && uf ? `${cidade}/${uf}` : cidade || uf,
    cep ? `CEP ${maskCEP(cep)}` : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

/* ================== Cores para Sinais-Chave ================== */
function signalColor(value, type) {
  // retorna classes tailwind (texto)
  // type pode ajudar em regras específicas (ex.: consistência invertida)
  const v = (value || "").toUpperCase();

  // mapa genérico: verde (bom), amarelo (médio), vermelho (ruim)
  const GREEN = "text-emerald-600";
  const AMBER = "text-amber-600";
  const RED = "text-rose-600";

  if (type === "gravidadeAtual") {
    // BAIXA (verde), MÉDIA (âmbar), ALTA/CRÍTICA (vermelho)
    if (v === "BAIXA") return GREEN;
    if (v === "MÉDIA" || v === "MEDIA") return AMBER;
    if (v === "ALTA" || v === "CRÍTICA" || v === "CRITICA") return RED;
  }

  if (type === "recenciaOcorrencias") {
    // ANTIGA (verde), INTERMEDIÁRIA (âmbar), RECENTE (vermelho)
    if (v === "ANTIGA") return GREEN;
    if (v === "INTERMEDIÁRIA" || v === "INTERMEDIARIA") return AMBER;
    if (v === "RECENTE") return RED;
  }

  if (type === "concentracaoRisco") {
    // BAIXA (verde), MÉDIA (âmbar), ALTA (vermelho)
    if (v === "BAIXA") return GREEN;
    if (v === "MÉDIA" || v === "MEDIA") return AMBER;
    if (v === "ALTA") return RED;
  }

  if (type === "consistenciaOperacional") {
    // ALTA (verde), MÉDIA (âmbar), BAIXA (vermelho)
    if (v === "ALTA") return GREEN;
    if (v === "MÉDIA" || v === "MEDIA") return AMBER;
    if (v === "BAIXA") return RED;
  }

  // fallback
  return "text-gray-700 dark:text-gray-200";
}

/* ================== UI BÁSICA ================== */
const RISK_COLORS_BG = {
  low: "bg-emerald-50/70 border-emerald-200/70 dark:bg-emerald-900/20 dark:border-emerald-800/40",
  mid: "bg-amber-50/70 border-amber-200/70 dark:bg-amber-900/20 dark:border-amber-800/40",
  high: "bg-rose-50/70 border-rose-200/70 dark:bg-rose-900/20 dark:border-rose-800/40",
};

function RiskBadge({ risk }) {
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r ${
        RISK_COLORS[risk] || "from-slate-500 to-slate-700"
      } text-white shadow-md`}
    >
      <span className="w-2 h-2 rounded-full bg-white/90" />
      Risco: {risk || "—"}
    </span>
  );
}

function Tag({ children }) {
  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-slate-200 dark:border-slate-700">
      {children}
    </span>
  );
}

function SoftCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl bg-white/70 dark:bg-slate-900/60 border border-slate-200/60 dark:border-slate-800/60 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur ${className}`}
    >
      {children}
    </div>
  );
}

function SectionTitle({ icon, title, subtitle }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-600">
          {title}
        </h3>
      </div>
      {subtitle && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
      )}
    </div>
  );
}

function Divider() {
  return (
    <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-slate-700 my-6" />
  );
}

export function MotorCredito({ data }) {
  const d = data || {};
  const risco = d.classificacaoRisco || "—";
  const tipoSujeito = d?.dadosCadastrais?.cnpj ? "PJ" : "PF";
  const confianca = d?.confiancaDaAvaliacao?.grau || "—";

  const decisao = d?.recomendacaoDeCredito?.decisao || "—";
  const limite = d?.recomendacaoDeCredito?.limiteSugerido;
  const plano = d?.planoDeMonitoramento;

  const prazoDias =
    limite?.prazoMaxDias != null ? Number(limite?.prazoMaxDias) : null;

  // Textos de motivo
  const motivoDecisao =
    d?.recomendacaoDeCredito?.motivoDecisao ||
    d?.resumoExecutivo ||
    "* A decisão de negociação é de total responsabilidade do credor e de acordo com sua própria política de crédito.";

  const motivosAprovacao =
    d?.recomendacaoDeCredito?.motivosAprovacao ||
    d?.recomendacaoDeCredito?.condicoes ||
    [];
  const motivosReprovacao = d?.recomendacaoDeCredito?.motivosReprovacao || [];
  const alertas = d?.recomendacaoDeCredito?.alertas || [];

  const isAprovado = (decisao || "").toUpperCase().includes("APROV");
  const isNegado =
    (decisao || "").toUpperCase().includes("NEG") ||
    (decisao || "").toUpperCase().includes("REPROV");
  const isAprovadoComAlerta = (decisao || "")
    .toUpperCase()
    .includes("RESTRICOES");

  // Visual da decisão (botãozão de ação)
  let decisionLabel = "EM ANÁLISE";
  let decisionDesc = "Proposta em análise, aguardando decisão ou alçada.";
  let decisionClasses = "from-slate-500 to-slate-700";

  if (isNegado) {
    decisionLabel = "REPROVAR";
    decisionDesc =
      "A proposta não atende aos critérios mínimos da política de crédito.";
    decisionClasses = "from-rose-500 to-red-600";
  } else if (isAprovadoComAlerta) {
    decisionLabel = "CAUTELA";
    decisionDesc =
      "Aprovado dentro da política, mas com pontos de atenção que exigem monitoramento.";
    decisionClasses = "from-amber-500 to-orange-500";
  } else if (isAprovado) {
    decisionLabel = "APROVAR";
    decisionDesc = "Perfil enquadrado na política de crédito vigente.";
    decisionClasses = "from-emerald-500 to-teal-500";
  }

  // Mapeia risco em índice para visual
  const riskLevels = ["BAIXO", "MÉDIO", "ALTO", "ALTÍSSIMO"];
  const riskIndex = riskLevels.indexOf((risco || "").toUpperCase());

  // Faixa de prazo visual
  let prazoFaixaLabel = "Prazo não informado";
  let prazoFaixaClass = "bg-slate-100 text-slate-700 border border-slate-200";

  if (prazoDias != null) {
    if (prazoDias <= 30) {
      prazoFaixaLabel = `Prazo curto (${prazoDias} dias)`;
      prazoFaixaClass =
        "bg-emerald-50 text-emerald-800 border border-emerald-200";
    } else if (prazoDias <= 60) {
      prazoFaixaLabel = `Prazo médio (${prazoDias} dias)`;
      prazoFaixaClass = "bg-amber-50 text-amber-800 border border-amber-200";
    } else {
      prazoFaixaLabel = `Prazo longo (${prazoDias} dias)`;
      prazoFaixaClass = "bg-sky-50 text-sky-800 border border-sky-200";
    }
  }

  function MotivosLista({ title, itens, emptyLabel, color = "gray" }) {
    if (!itens || itens.length === 0) {
      return (
        <div className="text-sm text-gray-400 italic">
          {emptyLabel || "Sem motivos detalhados informados."}
        </div>
      );
    }
    return (
      <div>
        <div
          className={`text-base font-semibold text-gray-500 mb-1 text-${color}-600`}
        >
          {title}
        </div>
        <ul className="space-y-2 text-sm text-gray-700 mt-4">
          {itens.map((txt, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-gray-400" />
              <span>{txt?.fator}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function AlertasLista() {
    if (!alertas || alertas.length === 0) return null;
    return (
      <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/80 px-3 py-3">
        <div className="text-xs font-semibold text-amber-800 mb-1">
          Alertas para acompanhamento
        </div>
        <ul className="space-y-1 text-xs text-amber-900">
          {alertas.map((txt, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-amber-500" />
              <span>{txt}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function RiskTrack() {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
          <span>Nível de risco</span>
          <span className="font-medium text-gray-600">{risco}</span>
        </div>
        <div className="flex gap-1 bg-gray-200 rounded-full p-1">
          {riskLevels.map((rl, idx) => {
            const active = idx === riskIndex;
            return (
              <div
                key={rl}
                className={`flex-1 h-7 rounded-full flex items-center justify-center text-[10px] font-medium transition-all ${
                  active
                    ? "bg-gradient-to-r from-emerald-400 via-amber-400 to-rose-500 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.3)]"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {rl}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function LimitBar() {
    const valor = Number(limite?.valor || 0);
    // só pra dar visual: normaliza em 0–100k
    const pct = Math.max(
      5,
      Math.min(100, valor > 0 ? (valor / 100000) * 100 : 0)
    );

    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] text-gray-400">Limite sugerido</span>
          <span className="text-[11px] text-gray-500">
            Faixa ilustrativa até R$ 100 mil
          </span>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex items-baseline justify-between">
            <div className="text-lg font-semibold text-gray-800">
              {moneyBR(valor)}
            </div>
          </div>
          <div className="mt-2 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-2 rounded-full bg-gradient-to-r ${
                isNegado
                  ? "from-rose-500 to-red-600"
                  : isAprovadoComAlerta
                  ? "from-amber-400 to-orange-500"
                  : "from-emerald-400 to-teal-500"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div>
        {/* Header simples */}
        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="flex flex-row justify-between items-end gap-4 mb-2"
        >
          <div className="flex flex-col gap-1">
            <div className="font-semibold">
              {data?.dadosCadastrais?.razaoSocial || "Cliente"}
            </div>
            <div className="text-gray-400 text-sm">
              {data?.dadosCadastrais?.cnpj
                ? `CNPJ: ${formatDocument(data?.dadosCadastrais?.cnpj)}`
                : ""}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <RiskBadge risk={risco} />
            <Tag>{confianca} confiança</Tag>
          </div>
        </motion.header>

        {/* DECISÃO EM DESTAQUE (full width) */}
        <SoftCard className="mb-3 p-0 overflow-hidden">
          <div
            className={`bg-gradient-to-r ${decisionClasses} px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3`}
          >
            <div>
              <div className="text-xs text-white/80 uppercase tracking-wide">
                Resultado da proposta
              </div>
              <div className="mt-1 text-2xl font-semibold text-white">
                {decisionLabel}
              </div>
              <div className="mt-1 text-xs md:text-sm text-white/85 max-w-xl">
                {decisionDesc}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="h-14 w-14 rounded-3xl bg-black/20 flex items-center justify-center text-2xl text-white shadow-lg">
                {isAprovadoComAlerta ? (
                  <HiExclamation />
                ) : isNegado ? (
                  <HiThumbDown />
                ) : isAprovado ? (
                  <HiThumbUp />
                ) : (
                  "…"
                )}
              </div>
              {/* <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/15 text-[11px] text-white border border-white/20">
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                {data?.dadosCadastrais?.razaoSocial || "Cliente"}
              </span> */}
            </div>
          </div>

          <div className="px-5 py-4 text-xs md:text-sm text-gray-700 bg-white">
            <span className="font-semibold">Resumo:</span> {motivoDecisao}
          </div>
        </SoftCard>

        <div className="mb-4 text-xs text-gray-500 italic px-2">
          *A decisão de negociação é de total responsabilidade do credor e de
          acordo com sua própria política de crédito.
        </div>

        {/* Grade com resumo e motivos */}
        <div className="grid md:grid-cols-2 gap-5">
          {/* Resumo rápido (100% visual) */}
          <SoftCard className="p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Resumo da operação
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Risco, limite e prazo em visão única.
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <RiskTrack />
              <LimitBar />
            </div>

            <Divider />

            <div className="text-[11px] text-gray-500">
              Esses parâmetros podem ser o “default” da política. Ajustes ficam
              a critério do comitê de crédito por segmento, canal ou ticket.
            </div>
          </SoftCard>

          {/* Motivos + Alertas + Condições */}
          <SoftCard className="p-4 md:p-5">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Por que o motor decidiu assim?
            </div>

            {isNegado ? (
              <MotivosLista
                title="Motivos da reprovação"
                itens={data?.explicabilidade?.topFatores}
                emptyLabel="Não foram informados motivos específicos, apenas a indicação de reprovação pela política."
                color="red"
              />
            ) : isAprovadoComAlerta ? (
              <MotivosLista
                title="Motivos da aprovação com alerta"
                itens={data?.explicabilidade?.topFatores}
                emptyLabel="Aprovado com alerta, mas sem motivos detalhados informados."
                color="orange"
              />
            ) : isAprovado ? (
              <MotivosLista
                title="Motivos da aprovação"
                itens={data?.explicabilidade?.topFatores}
                emptyLabel="Aprovado, mas sem motivos detalhados informados."
                color="green"
              />
            ) : (
              <div className="text-xs text-gray-400">
                A proposta ainda não está claramente aprovada nem reprovada.
                Registre nos campos de motivos para que o motor exiba aqui.
              </div>
            )}

            <AlertasLista />
          </SoftCard>
        </div>
      </div>
    </div>
  );
}
